/**
 * SkeletonBlock — manual fallback skeleton for areas where Boneyard
 * auto-capture is awkward (e.g. dialog interiors, edge cases).
 *
 * Uses the same `animate-pulse` approach as the shadcn Skeleton primitive
 * but adds layout-mirroring shapes. Respects `prefers-reduced-motion`.
 */
import { cn } from "@/lib/utils";

type Shape = "text" | "heading" | "circle" | "rect" | "card";

interface SkeletonBlockProps {
  /** Predefined shape shorthand */
  shape?: Shape;
  /** Width — CSS value (default: '100%') */
  w?: string;
  /** Height — CSS value (default: shape-dependent) */
  h?: string;
  /** Extra Tailwind classes */
  className?: string;
  /** Repeat this block N times (useful for list rows) */
  repeat?: number;
  /** Gap between repeated items (Tailwind spacing class, default: 'gap-3') */
  gap?: string;
}

const shapeDefaults: Record<Shape, { w: string; h: string; extra: string }> = {
  text:    { w: "100%", h: "0.875rem", extra: "rounded" },
  heading: { w: "40%",  h: "1.5rem",  extra: "rounded" },
  circle:  { w: "2.5rem", h: "2.5rem", extra: "rounded-full" },
  rect:    { w: "100%", h: "4rem",     extra: "rounded-lg" },
  card:    { w: "100%", h: "6rem",     extra: "rounded-xl" },
};

export function SkeletonBlock({
  shape = "rect",
  w,
  h,
  className,
  repeat = 1,
  gap = "gap-3",
}: SkeletonBlockProps) {
  const defaults = shapeDefaults[shape];

  const block = (key?: number) => (
    <div
      key={key}
      className={cn(
        "motion-safe:animate-pulse bg-muted",
        defaults.extra,
        className,
      )}
      style={{
        width: w ?? defaults.w,
        height: h ?? defaults.h,
      }}
      aria-hidden="true"
    />
  );

  if (repeat === 1) return block();

  return (
    <div className={cn("flex flex-col", gap)}>
      {Array.from({ length: repeat }, (_, i) => block(i))}
    </div>
  );
}

// ── Pre-composed skeleton layouts ─────────────────────────────────────────

/** Skeleton matching the DashboardCards 5-card KPI row */
export function DashboardCardsSkeleton() {
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          className="rounded-xl border bg-card p-6 space-y-3"
          aria-hidden="true"
        >
          <div className="flex items-center justify-between">
            <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-24" />
            <div className="motion-safe:animate-pulse bg-muted rounded-full h-4 w-4" />
          </div>
          <div className="motion-safe:animate-pulse bg-muted rounded h-8 w-20" />
          <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-32" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton matching the RecentBookings table */
export function BookingsTableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="rounded-xl border bg-card" aria-hidden="true">
      <div className="p-6 flex items-center justify-between">
        <div className="space-y-2">
          <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-44" />
          <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-56" />
        </div>
        <div className="motion-safe:animate-pulse bg-muted rounded h-9 w-20" />
      </div>
      <div className="px-6 pb-6">
        {/* Header row */}
        <div className="grid grid-cols-5 gap-4 pb-3 border-b">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="motion-safe:animate-pulse bg-muted rounded h-4 w-16" />
          ))}
        </div>
        {/* Data rows */}
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="grid grid-cols-5 gap-4 py-3 border-b last:border-0">
            <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-24" />
            <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-20" />
            <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-20" />
            <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-16" />
            <div className="motion-safe:animate-pulse bg-muted rounded-full h-5 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton matching the RevenueBreakdown card */
export function RevenueBreakdownSkeleton() {
  return (
    <div className="rounded-xl border bg-card" aria-hidden="true">
      <div className="p-6 space-y-2">
        <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-40" />
        <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-52" />
      </div>
      <div className="px-6 pb-6 space-y-4">
        <div className="text-center space-y-2">
          <div className="motion-safe:animate-pulse bg-muted rounded h-8 w-24 mx-auto" />
          <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-36 mx-auto" />
        </div>
        {Array.from({ length: 2 }, (_, i) => (
          <div key={i} className="space-y-2">
            <div className="flex justify-between">
              <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-28" />
              <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-16" />
            </div>
            <div className="motion-safe:animate-pulse bg-muted rounded-full h-2 w-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Skeleton for a full-page table view (Bookings, Clients).
 *  Uses overflow-x-auto + min-width so mobile matches the real table scroll. */
export function FullPageTableSkeleton({
  rows = 6,
  cols = 5,
}: {
  rows?: number;
  cols?: number;
}) {
  // Ensure min-width keeps columns readable on mobile (matches real table overflow)
  const minWidth = cols > 5 ? `${cols * 7}rem` : undefined;

  return (
    <div className="bg-white rounded-lg shadow" aria-hidden="true">
      <div className="px-6 py-4 border-b">
        <div className="flex justify-between items-center">
          <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-36" />
          <div className="motion-safe:animate-pulse bg-muted rounded h-9 w-28" />
        </div>
      </div>
      <div className="overflow-x-auto">
        <div style={{ minWidth }}>
          {/* Header */}
          <div className="grid gap-4 px-6 py-3 bg-gray-50" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
            {Array.from({ length: cols }, (_, i) => (
              <div key={i} className="motion-safe:animate-pulse bg-muted rounded h-3 w-16" />
            ))}
          </div>
          {/* Rows */}
          {Array.from({ length: rows }, (_, i) => (
            <div
              key={i}
              className="grid gap-4 px-6 py-4 border-b last:border-0"
              style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
            >
              {Array.from({ length: cols }, (_, j) => (
                <div key={j} className="motion-safe:animate-pulse bg-muted rounded h-4" style={{ width: `${60 + (j * 7) % 40}%` }} />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Skeleton for service cards list */
export function ServicesListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="space-y-4" aria-hidden="true">
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded-xl border bg-card p-5 flex items-center justify-between">
          <div className="space-y-2 flex-1">
            <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-32" />
            <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-48" />
            <div className="flex gap-4">
              <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-16" />
              <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-16" />
            </div>
          </div>
          <div className="motion-safe:animate-pulse bg-muted rounded h-8 w-16" />
        </div>
      ))}
    </div>
  );
}

/** Skeleton for the public business / provider page */
export function ProviderPageSkeleton() {
  return (
    <div className="min-h-screen flex flex-col items-center pt-16 px-4" aria-hidden="true">
      {/* Avatar */}
      <div className="motion-safe:animate-pulse bg-muted rounded-full h-20 w-20 mb-4" />
      {/* Name */}
      <div className="motion-safe:animate-pulse bg-muted rounded h-6 w-40 mb-2" />
      {/* Handle */}
      <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-28 mb-6" />
      {/* Bio */}
      <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-64 mb-8" />
      {/* Link buttons */}
      <div className="w-full max-w-md space-y-3">
        {Array.from({ length: 3 }, (_, i) => (
          <div key={i} className="motion-safe:animate-pulse bg-muted rounded-xl h-12 w-full" />
        ))}
      </div>
      {/* Book button */}
      <div className="motion-safe:animate-pulse bg-muted rounded-full h-12 w-full max-w-md mt-6" />
    </div>
  );
}

/** Skeleton for booking confirmation card */
export function BookingConfirmationSkeleton() {
  return (
    <div className="w-full max-w-2xl mx-auto" aria-hidden="true">
      <div className="rounded-xl border bg-card">
        <div className="p-6 space-y-3">
          <div className="motion-safe:animate-pulse bg-muted rounded h-8 w-full" />
          <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-44" />
        </div>
        <div className="px-6 pb-6 space-y-4">
          {/* Booking card placeholder */}
          <div className="motion-safe:animate-pulse bg-muted rounded-xl h-48 w-full" />
          {/* Detail grid */}
          <div className="grid grid-cols-2 gap-4">
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="space-y-1">
                <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-16" />
                <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-28" />
              </div>
            ))}
          </div>
          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <div className="motion-safe:animate-pulse bg-muted rounded h-10 w-32" />
            <div className="motion-safe:animate-pulse bg-muted rounded h-10 w-36" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Auth redirect skeleton — Figgy mark + shell */
export function AuthShellSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" aria-hidden="true">
      {/* Nav bar skeleton */}
      <div className="h-16 border-b bg-white flex items-center px-4">
        <div className="motion-safe:animate-pulse bg-muted rounded h-6 w-20" />
        <div className="ml-auto flex gap-3">
          <div className="motion-safe:animate-pulse bg-muted rounded h-8 w-8" />
          <div className="motion-safe:animate-pulse bg-muted rounded-full h-8 w-8" />
        </div>
      </div>
      {/* Page content area */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6 space-y-6">
        {/* Page header */}
        <div className="space-y-2">
          <div className="motion-safe:animate-pulse bg-muted rounded h-7 w-36" />
          <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-64" />
        </div>
        {/* KPI cards */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <div key={i} className="rounded-xl border bg-card p-6 space-y-3">
              <div className="motion-safe:animate-pulse bg-muted rounded h-4 w-24" />
              <div className="motion-safe:animate-pulse bg-muted rounded h-8 w-20" />
              <div className="motion-safe:animate-pulse bg-muted rounded h-3 w-32" />
            </div>
          ))}
        </div>
        {/* Content area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-xl border bg-card p-6 space-y-4">
            <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-44" />
            {Array.from({ length: 4 }, (_, i) => (
              <div key={i} className="motion-safe:animate-pulse bg-muted rounded h-10 w-full" />
            ))}
          </div>
          <div className="rounded-xl border bg-card p-6 space-y-4">
            <div className="motion-safe:animate-pulse bg-muted rounded h-5 w-36" />
            <div className="motion-safe:animate-pulse bg-muted rounded h-32 w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}
